import { BaseEntity, RunView } from "@memberjunction/core";
import { RegisterClass } from "@memberjunction/global";
import { BCSaaSChannelEntity } from "@bluecypress/bcsaas-entities";
import { IzzyChannelActionEntity, IzzyChannelExtensionEntity } from "../generated/entity_subclasses.js";

/**
 * Client-safe extended ChannelEntity.
 *
 * Post-BAC: Channel lives in __BCSaaS (bubbled up from Izzy in BCSaaS v1.3.x).
 * We extend BCSaaSChannelEntity and register against 'BC: Channels'.
 *
 * This class provides channel action management without credential encryption.
 * For server-side use with credential encryption, use the ChannelEntityExtended
 * from the izzy-lib package which registers with higher priority (3).
 */
@RegisterClass(BaseEntity, 'BC: Channels')
export class ChannelEntityExtended extends BCSaaSChannelEntity {
    /**
     * Channel Actions associated with this Channel (v2.0)
     * These replace the previous Tools/ChannelTools and link to __mj.Action
     */
    private _ChannelActions: IzzyChannelActionEntity[] = [];

    /**
     * The list of Channel Actions that this Channel supports
     * Actions are linked to __mj.Action and can include per-channel credentials
     */
    public get ChannelActions(): IzzyChannelActionEntity[] {
        return this._ChannelActions;
    }

    public set ChannelActions(value: IzzyChannelActionEntity[]) {
        this._ChannelActions = value;
    }

    /**
     * Gets a ChannelAction by its ID
     * @param id The ChannelAction ID
     * @returns The IzzyChannelActionEntity or null if not found
     */
    public GetChannelActionByID(id: string): IzzyChannelActionEntity | null {
        const action: IzzyChannelActionEntity | undefined = this._ChannelActions.find(ca => ca.ID === id);
        return action || null;
    }

    /**
     * Gets a ChannelAction by the linked Action ID
     * @param actionId The __mj.Action ID
     * @returns The IzzyChannelActionEntity or null if not found
     */
    public GetChannelActionByActionID(actionId: string): IzzyChannelActionEntity | null {
        const action: IzzyChannelActionEntity | undefined = this._ChannelActions.find(ca => ca.ActionID === actionId);
        return action || null;
    }

    /**
     * Gets all active ChannelActions sorted by Sequence
     * @returns Array of active IzzyChannelActionEntity sorted by Sequence
     */
    public GetActiveChannelActions(): IzzyChannelActionEntity[] {
        return this._ChannelActions
            .filter(ca => ca.Status === 'Active')
            .sort((a, b) => a.Sequence - b.Sequence);
    }

    /**
     * Cached Izzy.ChannelExtension entity for this channel (1:1 lookup by ChannelID).
     * Post-BAC: CredentialID/AIConfigurationID/ActionInheritMode moved off BCSaaS.Channel into
     * Izzy.ChannelExtension. These getters load the extension row on demand.
     */
    private _cachedExtension: IzzyChannelExtensionEntity | null = null;
    private _extensionLoaded: boolean = false;

    /**
     * Loads (or returns the cached) Izzy.ChannelExtension row for this channel.
     */
    protected async getExtension(): Promise<IzzyChannelExtensionEntity | null> {
        if (this._extensionLoaded) {
            return this._cachedExtension;
        }
        if (!this.ID) {
            this._extensionLoaded = true;
            this._cachedExtension = null;
            return null;
        }
        const rv = new RunView();
        const result = await rv.RunView<IzzyChannelExtensionEntity>({
            EntityName: 'Channel Extensions',
            ExtraFilter: `ChannelID = '${this.ID}'`,
            ResultType: 'entity_object'
        }, this.ContextCurrentUser);
        this._extensionLoaded = true;
        this._cachedExtension = (result?.Success && result.Results.length > 0) ? result.Results[0] : null;
        return this._cachedExtension;
    }

    /**
     * Async getter for the CredentialID (now lives on Izzy.ChannelExtension).
     */
    public async GetCredentialID(): Promise<string | null> {
        const ext = await this.getExtension();
        return ext?.CredentialID ?? null;
    }

    /**
     * Async getter for the AIConfigurationID (now lives on Izzy.ChannelExtension).
     */
    public async GetAIConfigurationID(): Promise<string | null> {
        const ext = await this.getExtension();
        return ext?.AIConfigurationID ?? null;
    }

    /**
     * Async getter for the ActionInheritMode (now lives on Izzy.ChannelExtension).
     */
    public async GetActionInheritMode(): Promise<string | null> {
        const ext = await this.getExtension();
        return ext?.ActionInheritMode ?? null;
    }

    /**
     * Clears the cached extension, forcing a fresh load on next access.
     */
    public InvalidateExtensionCache(): void {
        this._extensionLoaded = false;
        this._cachedExtension = null;
    }
}

export function LoadChannelEntityExtended(): void {
}
